import { useGetNotifications, useMarkNotificationRead, useMarkAllNotificationsRead, getGetNotificationsQueryKey } from "@workspace/api-client-react";
import { useAuth } from "@/hooks/use-auth";
import { Link } from "wouter";
import { Bell, CheckCheck, ChevronLeft, Settings2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

type PrefKey = "orderUpdates" | "promotions" | "communityReplies" | "newDeals";
type Prefs = Record<PrefKey, boolean>;

const PREF_ITEMS: { key: PrefKey; label: string; sub: string }[] = [
  { key: "orderUpdates",    label: "Order updates",      sub: "Confirmed, preparing, out for delivery" },
  { key: "newDeals",        label: "New deals",          sub: "Fresh local deals in your town" },
  { key: "communityReplies",label: "Community replies",  sub: "Replies to your posts and comments" },
  { key: "promotions",      label: "Promotions",         sub: "Occasional updates from Ayrshire Hub" },
];

function NotificationPrefsPanel() {
  const qc = useQueryClient();
  const [showPrefs, setShowPrefs] = useState(false);

  const { data: prefs } = useQuery<Prefs>({
    queryKey: ["notification-prefs"],
    queryFn: () => {
      const token = localStorage.getItem("ayrshire_token");
      return fetch("/api/notification-preferences", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).then(r => r.json()) as Promise<Prefs>;
    },
    staleTime: 60000,
  });

  const { mutate: savePrefs, isPending } = useMutation({
    mutationFn: (updates: Partial<Prefs>) => {
      const token = localStorage.getItem("ayrshire_token");
      return fetch("/api/notification-preferences", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(updates),
      });
    },
    onMutate: async (updates) => {
      await qc.cancelQueries({ queryKey: ["notification-prefs"] });
      const prev = qc.getQueryData<Prefs>(["notification-prefs"]);
      qc.setQueryData<Prefs>(["notification-prefs"], old => old ? { ...old, ...updates } : old);
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["notification-prefs"], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["notification-prefs"] }),
  });

  return (
    <div className="mt-6 border-t border-slate-100 pt-4">
      <button
        onClick={() => setShowPrefs(p => !p)}
        className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-800 transition-colors"
      >
        <Settings2 size={15} />
        Notification preferences
        <span className="text-slate-400 font-normal">{showPrefs ? "▲" : "▼"}</span>
      </button>
      {showPrefs && prefs && (
        <div className="mt-3 space-y-2">
          {PREF_ITEMS.map(item => (
            <div key={item.key} className="flex items-center justify-between py-2 px-3 rounded-xl border border-slate-100 bg-white">
              <div>
                <p className="text-sm font-medium text-slate-800">{item.label}</p>
                <p className="text-xs text-slate-400">{item.sub}</p>
              </div>
              <button
                role="switch"
                aria-checked={prefs[item.key]}
                onClick={() => savePrefs({ [item.key]: !prefs[item.key] })}
                disabled={isPending}
                className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 relative ${prefs[item.key] ? "bg-emerald-500" : "bg-slate-200"} disabled:opacity-50`}
              >
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${prefs[item.key] ? "left-5" : "left-1"}`} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const TYPE_LABELS: Record<string, string> = {
  new_lead: "New Lead",
  job_response: "Job Response",
  breaking_news: "Breaking News",
  event_approved: "Event Approved",
  deal_approved: "Deal Approved",
  review_approved: "Review Approved",
};

const TYPE_ICONS: Record<string, string> = {
  new_lead: "📋",
  job_response: "💼",
  breaking_news: "📰",
  event_approved: "📅",
  deal_approved: "🏷️",
  review_approved: "⭐",
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function Notifications() {
  const { user } = useAuth();
  const { data, isLoading, refetch } = useGetNotifications({ query: { queryKey: getGetNotificationsQueryKey(), enabled: !!user } });
  const { mutate: markRead } = useMarkNotificationRead();
  const { mutate: markAll } = useMarkAllNotificationsRead();

  const items = data?.items ?? [];
  const unread = data?.unreadCount ?? 0;

  const handleMarkRead = (id: number) => {
    markRead({ id }, { onSuccess: () => void refetch() });
  };

  const handleMarkAll = () => {
    markAll(undefined, { onSuccess: () => void refetch() });
  };

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">Please log in to see your notifications.</p>
        <Link href="/login">
          <button className="mt-4 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold">Log In</button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link href="/account">
            <button className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
          </Link>
          <div>
            <h1 className="text-xl font-bold text-foreground">Notifications</h1>
            {unread > 0 && (
              <p className="text-xs text-slate-500">{unread} unread</p>
            )}
          </div>
        </div>
        {unread > 0 && (
          <button
            onClick={handleMarkAll}
            className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors px-3 py-1.5 rounded-lg hover:bg-primary/5"
          >
            <CheckCheck className="w-3.5 h-3.5" />
            Mark all read
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-slate-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <Bell className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No notifications yet</p>
          <p className="text-xs text-slate-400 mt-1">You'll be notified about new leads, approvals, and news.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(n => (
            <button
              key={n.id}
              onClick={() => !n.read && handleMarkRead(n.id)}
              className={`w-full text-left rounded-2xl border px-4 py-3.5 transition-colors flex items-start gap-3 ${
                n.read
                  ? "bg-white border-slate-100 text-slate-500"
                  : "bg-white border-primary/20 shadow-sm"
              }`}
            >
              <span className="text-xl leading-none mt-0.5 flex-shrink-0">
                {TYPE_ICONS[n.type] ?? "🔔"}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${n.read ? "bg-slate-100 text-slate-500" : "bg-primary/10 text-primary"}`}>
                      {(n.type.startsWith("sales_prospect:") || String(n.type) === "sales_leaderboard") ? "Sales" : (TYPE_LABELS[n.type] ?? n.type)}
                    </span>
                    {!n.read && (
                      <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                    )}
                  </div>
                  <span className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0">{timeAgo(n.createdAt)}</span>
                </div>
                <p className={`text-sm font-medium mt-1 ${n.read ? "text-slate-500" : "text-slate-800"}`}>{n.title}</p>
                <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{n.message}</p>
                {String(n.type) === "sales_leaderboard" && <Link href={user.role === "admin" ? "/admin/sales?view=leaderboard" : "/sales?view=leaderboard"} className="inline-block text-xs text-primary underline mt-1">Open sales leaderboard</Link>}
                {user.role === "admin" && /^sales_prospect:\d+$/.test(n.type) && <Link href={`/admin/sales?prospect=${n.type.split(":")[1]}`} className="inline-block text-xs text-primary underline mt-1">Open prospect</Link>}
              </div>
            </button>
          ))}
        </div>
      )}

      {user && <NotificationPrefsPanel />}
    </div>
  );
}
